import {
    Engine,
    Scene,
    Label,
    Font,
    Color,
    Vector,
    vec,
    Keys,
    TextAlign,
    CoordPlane,
    Actor,
    ImageSource,
    ParticleEmitter,
    EmitterType
} from "excalibur";
import { LAYOUT } from "../config";
import { Resources } from "../resources";
import { SettingsPopup } from "../ui/SettingsPopup";
import { AudioManager } from "../systems/AudioManager";

// Load UI images
const backgroundImage = new ImageSource('/img/ui/introscreen01.png');
const characterImage = new ImageSource('/img/ui/introscreen_character.png');
const logoImage = new ImageSource('/img/ui/rileyvsviruslogo.png');

/**
 * TitleScene - Start Screen
 * 
 * Displays the game title and a "tap to start" prompt.
 * Designed for mobile with large touch target.
 * 
 * Implements responsive layout that adapts to:
 * - Portrait mode: Vertical stack (Title top, button bottom)
 * - Landscape mode: Side-by-side layout with safe zone constraints
 */
export class TitleScene extends Scene {
    private backgroundActor!: Actor;
    private characterActor!: Actor;
    private logoActor!: Actor;
    private title!: Label;
    private subtitle!: Label;
    private tapLabel!: Label;
    private instructions1!: Label;
    private instructions2!: Label;
    private dedication!: Label;
    private particles!: ParticleEmitter;
    private settingsButton!: Label;
    private settingsPopup!: SettingsPopup;
    private pulseTime: number = 0;
    private bobTime: number = 0;
    private resizeHandler: (() => void) | null = null;
    private activationTime: number = 0; // Tracks when scene became active

    override onInitialize(engine: Engine): void {
        // Load images first
        Promise.all([
            backgroundImage.load(),
            characterImage.load(),
            logoImage.load()
        ]).then(() => {
            this.createGraphics(engine);
            this.updateLayout(engine);
        }).catch(err => {
            console.warn('Failed to load some images:', err);
            this.updateLayout(engine);
        });

        // Create background actor (behind everything)
        this.backgroundActor = new Actor({
            pos: vec(0, 0),
            anchor: Vector.Half,
            z: -100
        });
        this.backgroundActor.transform.coordPlane = CoordPlane.Screen;
        this.add(this.backgroundActor);

        // Create liquid particles (behind character, in front of background)
        // Simulates gentle floating bubbles/particles in a liquid environment
        this.particles = new ParticleEmitter({
            pos: vec(0, 0),
            width: 2500, // Wide enough to cover any screen width
            height: 1000,
            emitterType: EmitterType.Rectangle,
            emitRate: 50, // Fewer particles for subtlety
            isEmitting: true,
            z: -50, // Between background (-100) and character (0)
            particle: {
                minSpeed: 10, // Slower for liquid feel
                maxSpeed: 30,
                minAngle: -Math.PI / 2 - 0.4, // Wider spread for organic drift
                maxAngle: -Math.PI / 2 + 0.4,
                opacity: 0.4, // More subtle
                fade: true,
                life: 12000, // Longer life for slow drift
                minSize: 1,
                maxSize: 8, // More size variety
                startSize: 5,
                endSize: 2,
                beginColor: Color.fromHex('#aaddff'),
                endColor: Color.fromHex('#4488cc'),
                acc: vec(Math.random() * 2 - 1, 0) // Very slight horizontal acceleration
            }
        });
        this.particles.transform.coordPlane = CoordPlane.Screen;
        this.add(this.particles);

        // Create character actor
        this.characterActor = new Actor({
            pos: vec(0, 0),
            anchor: Vector.Half,
            z: 0
        });
        this.characterActor.transform.coordPlane = CoordPlane.Screen;
        this.add(this.characterActor);

        // Create logo actor
        this.logoActor = new Actor({
            pos: vec(0, 0),
            anchor: Vector.Half,
            z: 1
        });
        this.logoActor.transform.coordPlane = CoordPlane.Screen;
        this.add(this.logoActor);

        // Title (fallback if logo doesn't load)
        this.title = new Label({
            text: 'RILEY vs VIRUS',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 48,
                bold: true,
                color: Color.White,
                textAlign: TextAlign.Center,
                shadow: { blur: 5, offset: vec(3, 3), color: Color.Black }
            }),
            anchor: Vector.Half,
        });
        this.title.transform.coordPlane = CoordPlane.Screen;
        this.title.graphics.visible = false; // Hidden when logo loads
        this.add(this.title);

        // Subtitle
        this.subtitle = new Label({
            // text: 'IMMUNE SYSTEM SIMULATOR',
            text: '',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 18,
                color: Color.fromHex('#4ade80'),
                textAlign: TextAlign.Center,
                shadow: { blur: 2, offset: vec(2, 2), color: Color.Black }
            }),
            anchor: Vector.Half,
        });
        this.subtitle.transform.coordPlane = CoordPlane.Screen;
        this.add(this.subtitle);

        // Tap to start (will pulse)
        this.tapLabel = new Label({
            text: 'TAP TO BEGIN RECOVERY',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 28,
                bold: true,
                color: Color.fromHex('#fbbf24'),
                textAlign: TextAlign.Center,
                quality: 4,
                shadow: { blur: 32, offset: vec(2, 2), color: Color.Black }
            }),
            anchor: Vector.Half,
        });
        this.tapLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.tapLabel);

        // Add hover effect for desktop (scale up on hover)
        this.tapLabel.on('pointerenter', () => {
            this.tapLabel.scale = vec(LAYOUT.BUTTON_HOVER_SCALE, LAYOUT.BUTTON_HOVER_SCALE);
        });
        this.tapLabel.on('pointerleave', () => {
            this.tapLabel.scale = vec(1, 1);
        });
        // Enable pointer events on graphics bounds
        this.tapLabel.pointer.useGraphicsBounds = true;

        // Instructions - with thick shadow for legibility over busy backgrounds
        this.instructions1 = new Label({
            // text: 'Touch/drag to move your Macrophage',
            text: 'A Get Well Game',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 28,
                color: Color.White,
                bold: true,
                textAlign: TextAlign.Center,
                shadow: { blur: 8, offset: vec(2, 2), color: Color.fromRGB(0, 0, 0, 0.9) }
            }),
            anchor: Vector.Half,
        });
        this.instructions1.transform.coordPlane = CoordPlane.Screen;
        this.add(this.instructions1);

        this.instructions2 = new Label({
            // text: 'Survive 5 minutes to break the fever!',
            text: 'for Riley Keith Mills',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 28,
                color: Color.White,
                textAlign: TextAlign.Center,
                bold: true,
                shadow: { blur: 8, offset: vec(2, 2), color: Color.fromRGB(0, 0, 0, 0.9) }
            }),
            anchor: Vector.Half,
        });
        this.instructions2.transform.coordPlane = CoordPlane.Screen;
        this.add(this.instructions2);

        // Dedication - with thick shadow for legibility
        this.dedication = new Label({
            text: 'By Consider Waid',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 28,
                color: Color.White,
                textAlign: TextAlign.Center,
                bold: true,
                shadow: { blur: 4, offset: vec(2, 2), color: Color.fromRGB(0, 0, 0, 1) }
            }),
            anchor: Vector.Half,
        });
        this.dedication.transform.coordPlane = CoordPlane.Screen;
        this.add(this.dedication);

        // Click/tap anywhere to start (with 500ms delay to prevent loader click from triggering)
        engine.input.pointers.primary.on('down', () => {
            const timeSinceActivation = Date.now() - this.activationTime;
            if (engine.currentSceneName === 'title' && !this.settingsPopup?.getIsVisible() && timeSinceActivation > 500) {
                engine.goToScene('instructions');
            }
        });

        // Keyboard support for desktop testing
        engine.input.keyboard.on('press', (evt) => {
            if (engine.currentSceneName === 'title' && !this.settingsPopup?.getIsVisible() && (evt.key === Keys.Space || evt.key === Keys.Enter)) {
                engine.goToScene('instructions');
            }
        });

        // Settings button (gear icon)
        this.settingsButton = new Label({
            text: '⚙',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Arial, sans-serif',
                size: 48,
                color: Color.White,
                textAlign: TextAlign.Center,
                shadow: { blur: 4, offset: vec(2, 2), color: Color.Black },
            }),
            anchor: Vector.Half,
            z: 50,
        });
        this.settingsButton.transform.coordPlane = CoordPlane.Screen;
        this.settingsButton.on('pointerdown', (evt) => {
            evt.cancel();
            this.settingsPopup.toggle();
        });
        this.settingsButton.on('pointerenter', () => {
            this.settingsButton.scale = vec(1.2, 1.2);
        });
        this.settingsButton.on('pointerleave', () => {
            this.settingsButton.scale = vec(1, 1);
        });
        this.settingsButton.pointer.useGraphicsBounds = true;
        this.add(this.settingsButton);

        // Create settings popup
        this.settingsPopup = new SettingsPopup(engine);

        // Initial layout
        this.updateLayout(engine);
    }

    /**
     * Create graphics once images are loaded
     */
    private createGraphics(engine: Engine): void {
        // Apply background
        if (backgroundImage.isLoaded()) {
            const bgSprite = backgroundImage.toSprite();
            this.backgroundActor.graphics.use(bgSprite);
        }

        // Apply character image
        if (characterImage.isLoaded()) {
            const charSprite = characterImage.toSprite();
            this.characterActor.graphics.use(charSprite);
        }

        // Apply logo
        if (logoImage.isLoaded()) {
            const logoSprite = logoImage.toSprite();
            this.logoActor.graphics.use(logoSprite);
            // Hide the text title when we have the logo
            this.title.graphics.visible = false;
        } else {
            // Show text title if logo didn't load
            this.title.graphics.visible = true;
        }
    }

    override onActivate(): void {
        // Track when the scene became active to prevent loader click from triggering
        this.activationTime = Date.now();

        // Listen for browser resize events
        this.resizeHandler = () => this.updateLayout(this.engine);
        this.engine.screen.events.on('resize', this.resizeHandler);

        // Update layout immediately in case screen size changed
        this.updateLayout(this.engine);

        // Play menu music using AudioManager
        AudioManager.playMenuMusic();
    }

    override onDeactivate(): void {
        // Cleanup listener
        if (this.resizeHandler) {
            this.engine.screen.events.off('resize', this.resizeHandler);
            this.resizeHandler = null;
        }

        // Stop music when leaving title (e.g. starting game)
        Resources.MenuMusic.stop();
    }

    /**
     * Responsive layout logic - positions elements based on screen orientation
     */
    private updateLayout(engine: Engine): void {
        const w = engine.drawWidth;
        const h = engine.drawHeight;
        const isPortrait = h > w;

        // Position particles emitter at bottom
        if (this.particles) {
            // this.particles.pos = vec(w / 2, h + 20);
            this.particles.pos = vec(0, 0);
        }

        // Position and scale background to cover screen (center-crop)
        if (this.backgroundActor && backgroundImage.isLoaded()) {
            this.backgroundActor.pos = vec(w / 2, h / 2);
            const bgWidth = backgroundImage.width;
            const bgHeight = backgroundImage.height;
            // Scale to cover screen while maintaining aspect ratio
            const scaleX = w / bgWidth;
            const scaleY = h / bgHeight;
            const scale = Math.max(scaleX, scaleY);
            this.backgroundActor.scale = vec(scale, scale);
        }

        if (isPortrait) {
            // --- PORTRAIT MODE (Vertical Stack) ---
            const centerX = w / 2;

            // Logo/Title at top 15%
            if (this.logoActor && logoImage.isLoaded()) {
                this.logoActor.pos = vec(centerX, h * 0.18);
                // Scale logo to fit width with padding
                const logoScale = Math.min(0.5, (w * 0.8) / logoImage.width);
                this.logoActor.scale = vec(logoScale, logoScale);
            }
            this.title.pos = vec(centerX, h * LAYOUT.PORTRAIT.TITLE_Y);

            // Subtitle just below title/logo
            this.subtitle.pos = vec(centerX, h * 0.25);

            // Character in middle
            if (this.characterActor && characterImage.isLoaded()) {
                this.characterActor.pos = vec(centerX, h * LAYOUT.PORTRAIT.CHARACTER_Y);
                const charScale = Math.min(LAYOUT.PORTRAIT.CHARACTER_SCALE, (w * 0.7) / characterImage.width);
                this.characterActor.scale = vec(charScale, charScale);
            }

            // Tap button at 85% (bottom area)
            this.tapLabel.pos = vec(centerX, h * LAYOUT.PORTRAIT.BUTTONS_Y);

            // Instructions above dedication at bottom
            this.instructions1.pos = vec(centerX, h - 120);
            this.instructions2.pos = vec(centerX, h - 80);
            this.dedication.pos = vec(centerX, h - 45);

        } else {
            // --- LANDSCAPE MODE (Centered with safe zone) ---
            const safeLeft = (w - Math.min(w, LAYOUT.MAX_CONTENT_WIDTH)) / 2;
            const safeWidth = Math.min(w, LAYOUT.MAX_CONTENT_WIDTH);

            // Character on left side
            if (this.characterActor && characterImage.isLoaded()) {
                const charX = safeLeft + safeWidth * LAYOUT.LANDSCAPE.CHARACTER_X;
                this.characterActor.pos = vec(charX, h * LAYOUT.LANDSCAPE.CHARACTER_Y);
                const charScale = Math.min(
                    LAYOUT.LANDSCAPE.CHARACTER_MAX_SCALE,
                    LAYOUT.LANDSCAPE.CHARACTER_SCALE * (h / 600)
                );
                this.characterActor.scale = vec(charScale, charScale);
            }

            // Logo/Title on right side
            const uiX = safeLeft + safeWidth * LAYOUT.LANDSCAPE.UI_X;
            if (this.logoActor && logoImage.isLoaded()) {
                this.logoActor.pos = vec(uiX, h * 0.25);
                const logoScale = Math.min(0.6, (safeWidth * 0.35) / logoImage.width);
                this.logoActor.scale = vec(logoScale, logoScale);
            }
            this.title.pos = vec(uiX, h * LAYOUT.LANDSCAPE.TITLE_Y);

            // Subtitle below title/logo
            this.subtitle.pos = vec(uiX, h * 0.35);

            // Tap button in lower right area
            this.tapLabel.pos = vec(uiX, h * LAYOUT.LANDSCAPE.BUTTONS_Y);

            // Instructions at bottom center
            this.instructions1.pos = vec(w / 2, h - 85);
            this.instructions2.pos = vec(w / 2, h - 60);
            this.dedication.pos = vec(w / 2, h - 35);
        }

        // Settings button in top-right corner
        this.settingsButton.pos = vec(w - 30, 30);

        // Update settings popup layout if visible
        if (this.settingsPopup?.getIsVisible()) {
            this.settingsPopup.updateLayout();
        }
    }

    override onPreUpdate(_engine: Engine, delta: number): void {
        this.pulseTime += delta;
        this.bobTime += delta;

        // Pulse the tap label
        const pulse = 0.6 + Math.sin(this.pulseTime / 300) * 0.4;
        if (this.tapLabel) {
            this.tapLabel.graphics.opacity = pulse;
        }

        // Bobbing animation for character and logo (Move Up/Down slightly)
        const bobOffset = Math.sin(this.bobTime / 800) * 10; // +/- 10px period 1600ms

        if (this.characterActor) {
            this.characterActor.graphics.offset = vec(0, bobOffset);
        }

        if (this.logoActor) {
            // Bob logo with slight phase shift
            const logoBob = Math.sin((this.bobTime / 800) + 1) * 5;
            this.logoActor.graphics.offset = vec(0, logoBob);
        }
    }
}
